import SlidingNumber from '@/components/sliding-number';
import { cn } from '@/lib/utils';
import { api } from '@/utils/api';
import { lightImpact, mediumImpact } from '@/utils/capacitorUtils';
import env from '@/utils/loadEnv';
import React, { useState } from 'react';

interface LikeButtonProps {
  postId: number;
  initialLiked: boolean;
  initialLikeCount: number;
  userId: string; // Changed to string for Firebase UID
  showCount?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onLikeChange?: (isLiked: boolean, count: number) => void;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  postId,
  initialLiked,
  initialLikeCount,
  size = 'md',
  className,
  onLikeChange,
}) => {
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiking, setIsLiking] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);


  const handleLike = async () => {
    if (isLiking) return;

    setIsLiking(true);

    // Only animate when going from unliked to liked
    if (!isLiked) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 600);
    }

    try {
      // Update UI immediately for better UX
      setIsLiked(prevLiked => !prevLiked);

      if (isLiked) {
        mediumImpact();
        setLikeCount(prevCount => prevCount - 1);
        await api.delete(`${env.VITE_PUBLIC_API_URL}/likes/posts/${postId}`);
      } else {
        setLikeCount(prevCount => prevCount + 1);
        await api.post(`${env.VITE_PUBLIC_API_URL}/likes/posts/${postId}`);
      }

      // Notify parent component if callback provided
      if (onLikeChange) {
        onLikeChange(!isLiked, isLiked ? likeCount - 1 : likeCount + 1);
      }
    } catch (error) {
      console.error('Error liking/unliking post:', error);
      // Revert UI changes if API call fails
      setIsLiked(isLiked);
      setLikeCount(likeCount);
    } finally {
      setIsLiking(false);
      lightImpact();
    }
  };

  // Determine icon size based on the size prop
  const iconSize = size === 'sm' ? 'size-5' : size === 'lg' ? 'size-7' : 'size-6';
  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';

  return (
    <button
      onClick={handleLike}
      aria-label={isLiked ? "Unlike" : "Like"}
      aria-pressed={isLiked}
      disabled={isLiking}
      className={cn(
        "group relative flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2",
        className
      )}
    >
      <span className="relative inline-block">
        {/* Custom heart SVG */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          className={cn(
            iconSize,
            "transition-all duration-0",
            isLiked ? "text-rose-500" : "text-foreground group-hover:text-rose-400",
            isAnimating ? "animate-scale-heart" : ""
          )}
          fill={isLiked ? "currentColor" : "none"}
        >
          <path
            d="M19.4626 3.99415C16.7809 2.34923 14.4404 3.01211 13.0344 4.06801C12.4578 4.50096 12.1696 4.71743 12 4.71743C11.8304 4.71743 11.5422 4.50096 10.9656 4.06801C9.55962 3.01211 7.21909 2.34923 4.53744 3.99415C1.01807 6.15294 0.221721 13.2749 8.33953 19.2834C9.88572 20.4278 10.6588 21 12 21C13.3412 21 14.1143 20.4278 15.6605 19.2834C23.7783 13.2749 22.9819 6.15294 19.4626 3.99415Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>

        {/* Burst effect - only when liking */}
        {isAnimating && (
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className={cn(
                "animate-scale-burst text-rose-500",
                size === 'sm' ? 'size-8' : size === 'lg' ? 'size-14' : 'size-10'
              )}
              viewBox="0 0 24 24"
              fill="rgba(244, 63, 94, 0.5)"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.4626 3.99415C16.7809 2.34923 14.4404 3.01211 13.0344 4.06801C12.4578 4.50096 12.1696 4.71743 12 4.71743C11.8304 4.71743 11.5422 4.50096 10.9656 4.06801C9.55962 3.01211 7.21909 2.34923 4.53744 3.99415C1.01807 6.15294 0.221721 13.2749 8.33953 19.2834C9.88572 20.4278 10.6588 21 12 21C13.3412 21 14.1143 20.4278 15.6605 19.2834C23.7783 13.2749 22.9819 6.15294 19.4626 3.99415Z"
                stroke="none"
              />
            </svg>
          </span>
        )}
      </span>

      {/* Like count */}
      {likeCount > 0 && (
        <span
          className={cn(
            textSize,
            "font-medium tabular-nums transition-colors duration-300",
          )}
        >
          <SlidingNumber value={likeCount} />
        </span>
      )}
    </button>
  );
};

export default LikeButton;
